import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, Award, CheckCircle, ArrowRight, Send, Calculator, Percent, BadgePercent } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { ProformaRequestDialog } from "../components/ProformaRequestDialog";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CorporateTraining = () => {
  const [courses, setCourses] = useState([]);
  const [discountTiers, setDiscountTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Calculator state
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedFormat, setSelectedFormat] = useState("");
  const [participants, setParticipants] = useState(5);
  const [priceCalculation, setPriceCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Inquiry form state
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    employee_count: "",
    interested_courses: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, tiersRes] = await Promise.all([
          axios.get(`${API}/courses`, { params: { all: true } }),
          axios.get(`${API}/corporate/discount-tiers`)
        ]);
        setCourses(coursesRes.data);
        setDiscountTiers(tiersRes.data.tiers);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const calculatePrice = async () => {
    if (!selectedCourse || !selectedFormat || !participants) {
      toast.error("Please select a course, format, and number of participants");
      return;
    }
    
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/corporate/calculate-price`, null, {
        params: {
          course_id: selectedCourse,
          delivery_format: selectedFormat,
          participants: participants
        }
      });
      setPriceCalculation(response.data);
    } catch (error) {
      toast.error("Error calculating price");
    } finally {
      setCalculating(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = "Company name is required";
    if (!formData.contact_name.trim()) newErrors.contact_name = "Contact name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.employee_count) newErrors.employee_count = "Please select employee count";
    if (!formData.interested_courses.trim()) newErrors.interested_courses = "Please specify courses of interest";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/corporate/inquiry`, formData);
      setSubmitted(true);
      toast.success("Inquiry submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCourseData = courses.find(c => c.id === selectedCourse);

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-accent" />
          </div>
          <h2 className="font-heading font-bold text-2xl mb-4">Inquiry Submitted!</h2>
          <p className="text-muted-foreground mb-6">
            Thank you for your interest in corporate training. Our team will contact you within 24 hours to discuss your requirements.
          </p>
          <div className="space-y-4">
            <Link to="/courses">
              <Button className="w-full bg-brand hover:bg-brand-dark text-white">
                Browse Courses
              </Button>
            </Link>
            <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({ company_name: "", contact_name: "", email: "", phone: "", employee_count: "", interested_courses: "", notes: "" }); }}>
              Submit Another Inquiry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-br from-primary via-brand to-primary">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-accent/20 text-accent border-0 mb-4">
              Corporate Solutions
            </Badge>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white tracking-tight mb-6" data-testid="corporate-title">
              Corporate Training Programs
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Empower your team with PECB certified training. Get volume discounts for group enrollments 
              and customized training solutions for your organization.
            </p>
            <div className="flex flex-wrap gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <BadgePercent className="h-5 w-5 text-accent" />
                <span>Up to 25% Group Discount</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-accent" />
                <span>Flexible Group Sizes</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-accent" />
                <span>PECB Certified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Discount Tiers */}
      <section className="py-12 bg-white border-b" data-testid="discount-tiers-section">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="font-heading font-bold text-2xl text-center mb-8">Volume Discount Tiers</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {discountTiers.map((tier, index) => (
              <Card key={index} className="text-center border-brand/20 hover:border-brand/50 transition-colors" data-testid={`discount-tier-${index}`}>
                <CardContent className="p-6">
                  <div className="text-3xl font-heading font-bold text-brand mb-2">
                    {tier.discount_percent}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {tier.min_participants}{tier.max_participants ? `-${tier.max_participants}` : "+"} participants
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Price Calculator */}
          <div>
            <Card data-testid="price-calculator">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-brand" />
                  Price Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Select Course</Label>
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger data-testid="calc-course-select">
                      <SelectValue placeholder="Choose a course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedCourseData && (
                  <div className="space-y-2">
                    <Label>Delivery Format</Label>
                    <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                      <SelectTrigger data-testid="calc-format-select">
                        <SelectValue placeholder="Choose format" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCourseData.delivery_formats?.map((format) => (
                          <SelectItem key={format.format} value={format.format}>
                            {format.format.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())} - ${format.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Number of Participants</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={participants}
                    onChange={(e) => setParticipants(parseInt(e.target.value) || 1)}
                    data-testid="calc-participants"
                  />
                </div>

                <Button 
                  onClick={calculatePrice} 
                  className="w-full bg-brand hover:bg-brand-dark text-white"
                  disabled={calculating}
                  data-testid="calc-button"
                >
                  {calculating ? "Calculating..." : "Calculate Price"}
                </Button>

                {priceCalculation && (
                  <div className="bg-slate-50 rounded-lg p-6 space-y-3" data-testid="calc-result">
                    <h4 className="font-heading font-semibold">{priceCalculation.course_title}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Format:</span>
                        <span>{priceCalculation.delivery_format.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Price per person:</span>
                        <span>${priceCalculation.base_price_per_person}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Participants:</span>
                        <span>{priceCalculation.participants}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span>${priceCalculation.subtotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                      </div>
                      {priceCalculation.discount_percent > 0 && (
                        <div className="flex justify-between text-accent">
                          <span>Discount ({priceCalculation.discount_percent}%):</span>
                          <span>-${priceCalculation.discount_amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between font-heading font-bold text-lg">
                        <span>Total:</span>
                        <span className="text-brand">${priceCalculation.total_price.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                      </div>
                      {priceCalculation.discount_percent > 0 && (
                        <div className="text-xs text-muted-foreground text-right">
                          ${priceCalculation.price_per_person_after_discount.toFixed(2)} per person
                        </div>
                      )}
                    </div>
                    <Link to={`/checkout/${selectedCourse}?format=${selectedFormat}&participants=${participants}&corporate=true`}>
                      <Button className="w-full mt-4 bg-accent hover:bg-accent/90 text-white">
                        Proceed to Checkout
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <ProformaRequestDialog courseId={selectedCourse} format={selectedFormat} participants={participants} triggerClassName="w-full" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Benefits */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="font-heading">Corporate Training Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {[
                    "Volume discounts up to 25% for groups",
                    "Dedicated account manager",
                    "Flexible scheduling options",
                    "Progress tracking and reporting",
                    "Custom training programs available",
                    "Invoice payment options",
                    "Post-training support"
                  ].map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Inquiry Form */}
          <div>
            <Card data-testid="inquiry-form">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-brand" />
                  Request Corporate Training Quote
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company Name *</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleInputChange}
                        placeholder="Your company"
                        className={errors.company_name ? "border-destructive" : ""}
                        data-testid="inquiry-company"
                      />
                      {errors.company_name && <p className="text-sm text-destructive">{errors.company_name}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Contact Name *</Label>
                      <Input
                        id="contact_name"
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleInputChange}
                        placeholder="Your name"
                        className={errors.contact_name ? "border-destructive" : ""}
                        data-testid="inquiry-contact"
                      />
                      {errors.contact_name && <p className="text-sm text-destructive">{errors.contact_name}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="email@company.com"
                        className={errors.email ? "border-destructive" : ""}
                        data-testid="inquiry-email"
                      />
                      {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (876) 123-4567"
                        className={errors.phone ? "border-destructive" : ""}
                        data-testid="inquiry-phone"
                      />
                      {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="employee_count">Number of Employees to Train *</Label>
                    <Select
                      value={formData.employee_count}
                      onValueChange={(value) => {
                        setFormData(prev => ({ ...prev, employee_count: value }));
                        if (errors.employee_count) setErrors(prev => ({ ...prev, employee_count: null }));
                      }}
                    >
                      <SelectTrigger className={errors.employee_count ? "border-destructive" : ""} data-testid="inquiry-employees">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3-5">3-5 employees</SelectItem>
                        <SelectItem value="6-10">6-10 employees</SelectItem>
                        <SelectItem value="11-25">11-25 employees</SelectItem>
                        <SelectItem value="26-50">26-50 employees</SelectItem>
                        <SelectItem value="50+">50+ employees</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.employee_count && <p className="text-sm text-destructive">{errors.employee_count}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interested_courses">Courses of Interest *</Label>
                    <Textarea
                      id="interested_courses"
                      name="interested_courses"
                      value={formData.interested_courses}
                      onChange={handleInputChange}
                      placeholder="E.g., ISO 9001 Lead Auditor, ISO 22000 Foundation..."
                      rows={3}
                      className={errors.interested_courses ? "border-destructive" : ""}
                      data-testid="inquiry-courses"
                    />
                    {errors.interested_courses && <p className="text-sm text-destructive">{errors.interested_courses}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Any specific requirements or questions..."
                      rows={3}
                      data-testid="inquiry-notes"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-brand hover:bg-brand-dark text-white"
                    disabled={submitting}
                    data-testid="inquiry-submit"
                  >
                    {submitting ? "Submitting..." : (
                      <>
                        Submit Inquiry
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl lg:text-3xl mb-4">
            Ready to Train Your Team?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your corporate training needs. Our team will help you 
            find the perfect training solution for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand hover:bg-brand-dark text-white px-8">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/courses">
              <Button size="lg" variant="outline">
                Browse All Courses
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateTraining;
